import { ComponentDoc } from 'modelInterface/prototype/prototypeInterface';
import {
  BooleanOperator,
  Operator,
  QueryBuilderQuery,
  SearchContext,
  SearchResultDoc,
} from '@ardoq/api-types';
import { advancedSearchApi, api } from '@ardoq/api';
import { referenceOptionOperations } from 'models/utils/referenceOptionOperations';
import { queryBuilderOperations } from '@ardoq/query-builder';

type QueryResult = { label: string; value: string }[];

type SearchResultDocumentMapper = ({
  doc,
}: {
  doc: SearchResultDoc & ComponentDoc;
}) => {
  label: string;
  value: string;
};

type ReferenceSearchResultDocumentMapper = ({
  doc,
}: {
  doc: SearchResultDoc & any;
}) => {
  label: string;
  value: string;
  description?: string;
};

const getComponentSuggestionLoader =
  (
    workspaceIds: string[],
    componentTypeNames: string[],
    mapper: SearchResultDocumentMapper
  ) =>
  async (query: string): Promise<QueryResult> => {
    const requestBody: QueryBuilderQuery = {
      condition: BooleanOperator.AND,
      rules: [
        {
          id: 'type',
          field: 'type',
          type: 'string',
          input: 'select',
          operator: Operator.EQUAL,
          value: SearchContext.COMPONENT,
        },
        {
          condition: BooleanOperator.AND,
          rules: [
            {
              id: 'name',
              field: 'name',
              input: 'text',
              type: 'string',
              operator: Operator.CONTAINS_SUBSTRING,
              value: query,
            },
            ...getTraversalScope(workspaceIds, componentTypeNames),
          ],
        },
      ],
    };

    const { results } = api.logErrorIfNeeded(
      await advancedSearchApi.search<SearchResultDoc & ComponentDoc>(
        requestBody,
        { size: 500 }
      )
    ) || { results: [] };

    return results.map(mapper);
  };

const getComponentParentSuggestionLoader =
  (workspaceIds: string[], componentTypeNames: string[]) =>
  async (query: string): Promise<QueryResult> => {
    const requestBody: QueryBuilderQuery = {
      condition: BooleanOperator.AND,
      rules: [
        {
          id: 'type',
          field: 'type',
          type: 'string',
          input: 'select',
          operator: Operator.EQUAL,
          value: SearchContext.COMPONENT,
        },
        {
          condition: BooleanOperator.AND,
          rules: [
            {
              field: 'child-count',
              id: 'child-count',
              input: 'number',
              operator: Operator.GREATER,
              type: 'number',
              value: 0,
            },
            {
              id: 'name',
              field: 'name',
              input: 'text',
              type: 'string',
              operator: Operator.CONTAINS_SUBSTRING,
              value: query,
            },
            ...getTraversalScope(workspaceIds, componentTypeNames),
          ],
        },
      ],
    };

    const { results } = api.logErrorIfNeeded(
      await advancedSearchApi.search<SearchResultDoc & ComponentDoc>(
        requestBody,
        { size: 500 }
      )
    ) || { results: [] };

    return results.map(({ doc: { name, _id } }) => ({
      label: name,
      value: _id,
    }));
  };

const getTraversalScope = (
  workspaceIds: string[],
  componentTypeNames: string[]
) => [
  {
    condition: BooleanOperator.OR,
    rules: workspaceIds.map(rootWorkspace => ({
      id: 'rootWorkspace',
      field: 'rootWorkspace',
      input: 'text',
      type: 'string',
      operator: Operator.EQUAL,
      value: rootWorkspace,
    })),
  },
  {
    condition: BooleanOperator.OR,
    rules: componentTypeNames.map(typeName => ({
      id: 'typeName',
      field: 'typeName',
      input: 'text',
      type: 'string',
      operator: Operator.EQUAL,
      value: typeName,
    })),
  },
];

const getReferenceSuggestionLoader =
  (workspaceIds: string[], mapper: ReferenceSearchResultDocumentMapper) =>
  async (query: string): Promise<QueryResult> => {
    const requestBody: QueryBuilderQuery = {
      condition: BooleanOperator.AND,
      rules: [
        {
          id: 'type',
          field: 'type',
          type: 'string',
          input: 'select',
          operator: Operator.EQUAL,
          value: SearchContext.REFERENCE,
        },
        {
          condition: BooleanOperator.AND,
          rules: [
            {
              id: 'id',
              field: '_id',
              input: 'text',
              type: 'string',
              operator: Operator.CONTAINS_SUBSTRING,
              value: query,
            },
            {
              condition: BooleanOperator.OR,
              rules: workspaceIds.map(
                queryBuilderOperations.getRootWorkspaceQueryRule
              ),
            },
          ],
        },
      ],
    };

    const { results } = api.logErrorIfNeeded(
      await advancedSearchApi.search<SearchResultDoc & any>(requestBody, {
        size: 500,
      })
    ) || { results: [] };

    return results.map(mapper);
  };

export const getViewpointComponentSuggestionLoaders = (
  workspaceIds: string[],
  componentTypeNames: string[]
) => ({
  loadComponentSuggestionsAsync: getComponentSuggestionLoader(
    workspaceIds,
    componentTypeNames,
    ({ doc: { name } }) => ({
      label: name,
      value: name,
    })
  ),
  loadParentComponentSuggestionsAsync: getComponentParentSuggestionLoader(
    workspaceIds,
    componentTypeNames
  ),
  loadComponentSuggestionsIncludingCurrentComponentAsync:
    getComponentSuggestionLoader(
      workspaceIds,
      componentTypeNames,
      ({ doc: { name, _id } }) => ({
        label: name,
        value: _id,
        description: _id,
      })
    ),
});

export const getViewpointReferenceSuggestionLoaders = (
  workspaceIds: string[]
) => ({
  loadReferenceOIDSuggestionsAsync: getReferenceSuggestionLoader(
    workspaceIds,
    ({ doc }) => {
      if (doc.displayText) {
        return {
          label: doc.displayText,
          value: doc._id,
          description: doc._id,
        };
      }

      const { sourceName, targetName, entityType } = doc || {};

      return {
        label:
          referenceOptionOperations.toReferenceLabelWithDisplayTextOrTypeAndComponentNames(
            {
              reference: { ...doc, typeName: entityType ?? 'Unknown' },
              sourceComponentName: sourceName ?? 'Unknown',
              targetComponentName: targetName ?? 'Unknown',
            }
          ),
        value: doc._id,
        description: doc._id,
      };
    }
  ),
});
